import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ship, Plus, Search, Filter, Edit2, Trash2, Eye, X,
  Anchor, Users, Calendar, ChevronDown, MoreVertical, 
  Package, Building2, AlertCircle, Sparkles, Check, ChevronRight,
  ChevronLeft, Loader2, AlertTriangle, CheckCircle2, XCircle,
  ArrowRight, Briefcase
} from 'lucide-react';
import { crewApi } from '../services/api';
import type { 
  Vessel, VesselCategory, CrewMember, ReliefPlan, VesselFormData,
  VesselPositionSuggestion 
} from '../types';
import { VESSEL_CATEGORIES } from '../types';

// Modal Component
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md'
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`relative bg-white rounded-2xl shadow-xl ${sizes[size]} w-full`}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6">{children}</div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

// Simple Vessel Form (for editing)
const VesselForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading
}: {
  initialData?: Vessel;
  onSubmit: (data: VesselFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}) => {
  const [formData, setFormData] = useState<VesselFormData>({
    name: initialData?.name || '',
    teu_capacity: initialData?.teu_capacity || undefined,
    category: initialData?.category || '',
    fleet: initialData?.fleet || '',
    company: initialData?.company || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vessel Name *
        </label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter vessel name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            TEU Capacity
          </label>
          <input
            type="number"
            value={formData.teu_capacity || ''}
            onChange={(e) => setFormData({ ...formData, teu_capacity: parseInt(e.target.value) || undefined })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 15000"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Select category</option>
            {VESSEL_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label} ({cat.teu})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Fleet
          </label>
          <input
            type="text"
            value={formData.fleet || ''}
            onChange={(e) => setFormData({ ...formData, fleet: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Fleet name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company
          </label>
          <input
            type="text"
            value={formData.company || ''}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Company name"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : initialData ? 'Update Vessel' : 'Create Vessel'}
        </button>
      </div>
    </form>
  );
};

// Multi-step Vessel Creation Wizard with AI Position Suggestions
type CreateWizardStep = 'details' | 'positions' | 'review';

interface VesselCreationWizardProps {
  onComplete: (vessel: Vessel) => void;
  onCancel: () => void;
}

const VesselCreationWizard = ({ onComplete, onCancel }: VesselCreationWizardProps) => {
  const [currentStep, setCurrentStep] = useState<CreateWizardStep>('details');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingPositions, setIsGeneratingPositions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data
  const [vesselData, setVesselData] = useState<VesselFormData>({
    name: '',
    teu_capacity: undefined,
    category: '',
    fleet: '',
    company: '',
  });
  
  // Position suggestions
  const [positions, setPositions] = useState<(VesselPositionSuggestion & { accepted: boolean })[]>([]);
  const [positionStats, setPositionStats] = useState<{
    total_positions: number;
    total_crew_needed: number;
    critical_positions: number;
  } | null>(null);

  const steps = [
    { key: 'details' as CreateWizardStep, label: 'Vessel Details', icon: Ship },
    { key: 'positions' as CreateWizardStep, label: 'AI Positions', icon: Sparkles },
    { key: 'review' as CreateWizardStep, label: 'Review & Create', icon: CheckCircle2 },
  ];

  const getStepIndex = (step: CreateWizardStep) => steps.findIndex(s => s.key === step);

  // Generate AI position suggestions
  const generatePositions = async () => {
    if (!vesselData.category) {
      setError('Please select a vessel category');
      return;
    }
    
    setIsGeneratingPositions(true);
    setError(null);
    
    try {
      const response = await crewApi.suggestVesselPositions({
        vessel_category: vesselData.category,
        teu_capacity: vesselData.teu_capacity,
      });
      
      // Add accepted status to each position (default: all accepted)
      setPositions(response.suggestions.map(pos => ({
        ...pos,
        accepted: true,
      })));
      setPositionStats({
        total_positions: response.total_positions,
        total_crew_needed: response.total_crew_needed || response.total_positions,
        critical_positions: response.critical_positions,
      });
      setCurrentStep('positions');
    } catch (err) {
      console.error('Failed to generate positions:', err);
      setError('Failed to generate position suggestions. Please try again.');
    } finally {
      setIsGeneratingPositions(false);
    }
  };

  // Toggle position acceptance
  const togglePosition = (rank: string) => {
    setPositions(prev => prev.map(pos => 
      pos.rank === rank ? { ...pos, accepted: !pos.accepted } : pos
    ));
  };

  // Accept all positions
  const acceptAll = () => {
    setPositions(prev => prev.map(pos => ({ ...pos, accepted: true })));
  };

  // Reject all positions
  const rejectAll = () => {
    setPositions(prev => prev.map(pos => ({ ...pos, accepted: false })));
  };

  // Create vessel with positions
  const createVessel = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const acceptedPositions = positions.filter(p => p.accepted);
      const response = await crewApi.createVesselWithPositions({
        ...vesselData,
        positions: acceptedPositions,
      });
      
      if (response.success) {
        onComplete(response.vessel);
      }
    } catch (err) {
      console.error('Failed to create vessel:', err);
      setError('Failed to create vessel. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const acceptedCount = positions.filter(p => p.accepted).length;

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8">
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = step.key === currentStep;
          const isCompleted = getStepIndex(currentStep) > index;
          
          return (
            <div key={step.key} className="flex items-center flex-1">
              <div className="flex items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center transition-colors
                  ${isActive ? 'bg-blue-600 text-white' : 
                    isCompleted ? 'bg-emerald-500 text-white' : 
                    'bg-gray-100 text-gray-400'}
                `}>
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <StepIcon className="w-5 h-5" />
                  )}
                </div>
                <span className={`ml-3 font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${isCompleted ? 'bg-emerald-500' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Step Content */}
      <AnimatePresence mode="wait">
        {currentStep === 'details' && (
          <motion.div
            key="details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vessel Name *
              </label>
              <input
                type="text"
                required
                value={vesselData.name}
                onChange={(e) => setVesselData({ ...vesselData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter vessel name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TEU Capacity
                </label>
                <input
                  type="number"
                  value={vesselData.teu_capacity || ''}
                  onChange={(e) => setVesselData({ ...vesselData, teu_capacity: parseInt(e.target.value) || undefined })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 15000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  required
                  value={vesselData.category}
                  onChange={(e) => setVesselData({ ...vesselData, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select category</option>
                  {VESSEL_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label} ({cat.teu})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fleet
                </label>
                <input
                  type="text"
                  value={vesselData.fleet || ''}
                  onChange={(e) => setVesselData({ ...vesselData, fleet: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Fleet name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  value={vesselData.company || ''}
                  onChange={(e) => setVesselData({ ...vesselData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Company name"
                />
              </div>
            </div>

            {/* AI Position Preview */}
            {vesselData.category && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">AI Position Suggestions</span>
                </div>
                <p className="text-sm text-blue-700">
                  Based on your selected category ({vesselData.category}), AI will suggest the required crew positions 
                  with priority levels and available crew counts.
                </p>
              </div>
            )}
          </motion.div>
        )}

        {currentStep === 'positions' && (
          <motion.div
            key="positions"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Summary Header */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">AI-Suggested Positions</h3>
                    <p className="text-sm text-gray-600">
                      {positionStats?.total_positions} position types • {positionStats?.total_crew_needed} crew members total • {positionStats?.critical_positions} critical
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={acceptAll}
                    className="px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-100 rounded-lg hover:bg-emerald-200 transition-colors"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={rejectAll}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Reject All
                  </button>
                </div>
              </div>
            </div>

            {/* Accepted Count */}
            <div className="text-sm text-gray-600">
              <span className="font-medium text-emerald-600">{acceptedCount}</span> of {positions.length} position types accepted
              {' • '}
              <span className="font-medium text-blue-600">
                {positions.filter(p => p.accepted).reduce((sum, p) => sum + p.suggested_count, 0)}
              </span> crew members
            </div>

            {/* Position List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {positions.map((position, index) => (
                <motion.div
                  key={position.rank}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 border rounded-xl transition-all ${
                    position.accepted 
                      ? 'bg-white border-emerald-200 shadow-sm' 
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Accept/Reject Toggle */}
                      <button
                        onClick={() => togglePosition(position.rank)}
                        className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          position.accepted 
                            ? 'bg-emerald-500 border-emerald-500 text-white' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {position.accepted && <Check className="w-4 h-4" />}
                      </button>

                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            {position.rank}
                          </span>
                          <h4 className="font-semibold text-gray-900">
                            {position.rank_name}
                            {position.suggested_count > 1 && (
                              <span className="ml-2 text-blue-600 font-bold">× {position.suggested_count}</span>
                            )}
                          </h4>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(position.priority)}`}>
                            {position.priority}
                          </span>
                          {position.required && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                              Required
                            </span>
                          )}
                          {position.suggested_count > 1 && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                              {position.suggested_count} needed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{position.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            Min. {Math.round(position.min_experience_months / 12)} years experience
                          </span>
                          <span className={`flex items-center gap-1 ${
                            position.available_crew_count >= position.suggested_count * 2 ? 'text-emerald-600' :
                            position.available_crew_count >= position.suggested_count ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            <Users className="w-3 h-3" />
                            {position.available_crew_count} available (need {position.suggested_count})
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">{position.reason}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {currentStep === 'review' && (
          <motion.div
            key="review"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Vessel Summary */}
            <div className="p-6 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-4 bg-blue-100 rounded-xl">
                  <Ship className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{vesselData.name}</h3>
                  <p className="text-gray-500">{vesselData.category} Vessel</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {vesselData.teu_capacity && (
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">TEU Capacity</div>
                    <div className="font-medium text-gray-900">{vesselData.teu_capacity.toLocaleString()} TEU</div>
                  </div>
                )}
                {vesselData.fleet && (
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Fleet</div>
                    <div className="font-medium text-gray-900">{vesselData.fleet}</div>
                  </div>
                )}
                {vesselData.company && (
                  <div className="p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Company</div>
                    <div className="font-medium text-gray-900">{vesselData.company}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Accepted Positions Summary */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Crew Positions ({acceptedCount} positions, {positions.filter(p => p.accepted).reduce((sum, p) => sum + p.suggested_count, 0)} crew total)
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                {positions.filter(p => p.accepted).map((position) => (
                  <div
                    key={position.rank}
                    className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {position.rank_name}
                        {position.suggested_count > 1 && (
                          <span className="ml-2 text-purple-600 font-bold">× {position.suggested_count}</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{position.rank}</div>
                    </div>
                    {position.suggested_count > 1 && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                        {position.suggested_count}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${getPriorityColor(position.priority)}`}>
                      {position.priority}
                    </span>
                  </div>
                ))}
              </div>

              {acceptedCount === 0 && (
                <div className="text-center py-8 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-amber-700">No positions selected. The vessel will be created without crew positions.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-gray-100">
        <button
          onClick={() => {
            if (currentStep === 'details') {
              onCancel();
            } else if (currentStep === 'positions') {
              setCurrentStep('details');
            } else {
              setCurrentStep('positions');
            }
          }}
          className="flex items-center gap-2 px-6 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          {currentStep === 'details' ? 'Cancel' : 'Back'}
        </button>

        {currentStep === 'details' && (
          <button
            onClick={generatePositions}
            disabled={!vesselData.name || !vesselData.category || isGeneratingPositions}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPositions ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Positions
              </>
            )}
          </button>
        )}

        {currentStep === 'positions' && (
          <button
            onClick={() => setCurrentStep('review')}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            Review & Create
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {currentStep === 'review' && (
          <button
            onClick={createVessel}
            disabled={isLoading}
            className="flex items-center gap-2 px-8 py-2.5 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                Create Vessel
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Vessel Detail View Component
const VesselDetailView = ({
  vessel,
  crew,
  reliefPlans,
  onClose,
  onEdit
}: {
  vessel: Vessel;
  crew: CrewMember[];
  reliefPlans: ReliefPlan[];
  onClose: () => void;
  onEdit: () => void;
}) => {
  const getCategoryColor = (category?: string) => {
    switch (category?.toLowerCase()) {
      case 'very large': return 'bg-blue-100 text-blue-700';
      case 'large': return 'bg-emerald-100 text-emerald-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'small': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Vessel Info */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-blue-100 rounded-xl">
            <Ship className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{vessel.name}</h3>
            <div className="flex items-center gap-3 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(vessel.category)}`}>
                {vessel.category || 'Unknown'}
              </span>
              {vessel.teu_capacity && (
                <span className="text-sm text-gray-500">
                  <Package className="w-4 h-4 inline mr-1" />
                  {vessel.teu_capacity.toLocaleString()} TEU
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Edit2 className="w-4 h-4" />
          Edit
        </button>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-500">Fleet</div>
          <div className="text-lg font-medium text-gray-900">{vessel.fleet || '-'}</div>
        </div>
        <div className="p-4 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-500">Company</div>
          <div className="text-lg font-medium text-gray-900">{vessel.company || '-'}</div>
        </div>
      </div>

      {/* Current Crew */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="w-5 h-5 mr-2 text-blue-600" />
          Current Crew ({crew.length})
        </h4>
        {crew.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl">
            <Users className="w-10 h-10 mx-auto mb-2" />
            <p>No crew currently assigned</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {crew.map((member) => (
              <div
                key={member.cid}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-600">
                      {member.rank?.substring(0, 2)}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {member.name} {member.surname}
                    </div>
                    <div className="text-sm text-gray-500">{member.rank}</div>
                  </div>
                </div>
                {member.due_date && (
                  <div className="text-sm text-gray-500">
                    Due: {member.due_date}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Relief Plans */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-blue-600" />
          Upcoming Relief Plans ({reliefPlans.length})
        </h4>
        {reliefPlans.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-xl">
            <Calendar className="w-10 h-10 mx-auto mb-2" />
            <p>No relief plans scheduled</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {reliefPlans.map((plan) => (
              <div
                key={plan.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900">{plan.rank}</div>
                  <div className="text-sm text-gray-500">
                    {plan.outgoing_name} → {plan.incoming_name || 'Pending'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{plan.due_date}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    plan.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    plan.status === 'overdue' ? 'bg-red-100 text-red-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {plan.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Vessels Page
export default function VesselsPage() {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);
  const [vesselCrew, setVesselCrew] = useState<CrewMember[]>([]);
  const [vesselReliefPlans, setVesselReliefPlans] = useState<ReliefPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadVessels();
  }, [categoryFilter]);

  const loadVessels = async () => {
    setIsLoading(true);
    try {
      const response = await crewApi.getVessels({ category: categoryFilter || undefined });
      setVessels(response.vessels);
    } catch (error) {
      console.error('Failed to load vessels:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadVesselDetails = async (vessel: Vessel) => {
    try {
      const details = await crewApi.getVesselById(vessel.id);
      setSelectedVessel(details.vessel);
      setVesselCrew(details.current_crew);
      setVesselReliefPlans(details.upcoming_relief_plans);
    } catch (error) {
      console.error('Failed to load vessel details:', error);
      setSelectedVessel(vessel);
      setVesselCrew([]);
      setVesselReliefPlans([]);
    }
  };

  const handleViewVessel = async (vessel: Vessel) => {
    await loadVesselDetails(vessel);
    setIsViewModalOpen(true);
  };

  const handleEditVessel = () => {
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleCreateVessel = async (vessel: Vessel) => {
    // Vessel was created by the wizard
    setIsCreateModalOpen(false);
    loadVessels();
  };

  const handleUpdateVessel = async (data: VesselFormData) => {
    if (!selectedVessel) return;
    setIsSaving(true);
    try {
      await crewApi.updateVessel(selectedVessel.id, data);
      setIsEditModalOpen(false);
      loadVessels();
    } catch (error) {
      console.error('Failed to update vessel:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteVessel = async (vessel: Vessel) => {
    if (!confirm(`Are you sure you want to delete ${vessel.name}?`)) return;
    try {
      await crewApi.deleteVessel(vessel.id);
      loadVessels();
    } catch (error) {
      console.error('Failed to delete vessel:', error);
    }
  };

  const filteredVessels = vessels.filter((vessel) =>
    vessel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryColor = (category?: string) => {
    switch (category?.toLowerCase()) {
      case 'very large': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'large': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'small': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Ship className="w-8 h-8 mr-3 text-blue-600" />
              Vessel Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage fleet vessels and their crew assignments
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl shadow-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Vessel
          </motion.button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search vessels..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {VESSEL_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Category Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {VESSEL_CATEGORIES.map((cat) => {
            const count = vessels.filter(v => v.category === cat.value).length;
            return (
              <motion.div
                key={cat.value}
                whileHover={{ scale: 1.02 }}
                onClick={() => setCategoryFilter(categoryFilter === cat.value ? '' : cat.value)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  categoryFilter === cat.value 
                    ? getCategoryColor(cat.value) 
                    : 'bg-white border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-sm font-medium">{cat.label}</div>
                    <div className="text-xs text-gray-500">{cat.teu}</div>
                  </div>
                  <Ship className="w-8 h-8 opacity-50" />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Vessels Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : filteredVessels.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <Ship className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No vessels found</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery ? 'Try adjusting your search' : 'Add your first vessel to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
              >
                Add Vessel
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVessels.map((vessel, index) => (
              <motion.div
                key={vessel.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${getCategoryColor(vessel.category)}`}>
                        <Ship className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{vessel.name}</h3>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(vessel.category)}`}>
                          {vessel.category || 'Unclassified'}
                        </span>
                      </div>
                    </div>
                    <div className="relative group">
                      <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                        <MoreVertical className="w-5 h-5 text-gray-400" />
                      </button>
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                        <button
                          onClick={() => handleViewVessel(vessel)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Eye className="w-4 h-4" /> View Details
                        </button>
                        <button
                          onClick={() => {
                            setSelectedVessel(vessel);
                            setIsEditModalOpen(true);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Edit2 className="w-4 h-4" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteVessel(vessel)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {vessel.teu_capacity && (
                      <div className="flex items-center text-gray-500">
                        <Package className="w-4 h-4 mr-2" />
                        {vessel.teu_capacity.toLocaleString()} TEU
                      </div>
                    )}
                    {vessel.fleet && (
                      <div className="flex items-center text-gray-500">
                        <Anchor className="w-4 h-4 mr-2" />
                        {vessel.fleet}
                      </div>
                    )}
                    {vessel.company && (
                      <div className="flex items-center text-gray-500 col-span-2">
                        <Building2 className="w-4 h-4 mr-2" />
                        {vessel.company}
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <button
                    onClick={() => handleViewVessel(vessel)}
                    className="w-full flex items-center justify-center gap-2 text-blue-600 font-medium hover:text-blue-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View Crew & Relief Plans
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredVessels.length > 0 && (
          <div className="mt-8 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Showing {filteredVessels.length} of {vessels.length} vessels
            </p>
          </div>
        )}
      </div>

      {/* Create Modal with Wizard */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add New Vessel"
        size="xl"
      >
        <VesselCreationWizard
          onComplete={handleCreateVessel}
          onCancel={() => setIsCreateModalOpen(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Vessel"
      >
        {selectedVessel && (
          <VesselForm
            initialData={selectedVessel}
            onSubmit={handleUpdateVessel}
            onCancel={() => setIsEditModalOpen(false)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Vessel Details"
        size="lg"
      >
        {selectedVessel && (
          <VesselDetailView
            vessel={selectedVessel}
            crew={vesselCrew}
            reliefPlans={vesselReliefPlans}
            onClose={() => setIsViewModalOpen(false)}
            onEdit={handleEditVessel}
          />
        )}
      </Modal>
    </div>
  );
}

